import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths } from 'date-fns';

const CALENDAR_TYPES = [
  { id: 'closing-current', type: 'closing', title: 'Closing Dates', monthOffset: 0 },
  { id: 'closing-next', type: 'closing', title: 'Closing Dates', monthOffset: 1 },
  { id: 'cd-current', type: 'cd', title: 'CD Must Go Out By', monthOffset: 0 },
  { id: 'cd-next', type: 'cd', title: 'CD Must Go Out By', monthOffset: 1 }
];

const CalendarDisplay = ({ events, darkMode, rotationInterval = 20 }) => {
  const [currentViewIndex, setCurrentViewIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const containerRef = React.useRef(null);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentViewIndex((prev) => (prev + 1) % CALENDAR_TYPES.length);
    }, rotationInterval * 1000);

    return () => clearInterval(interval);
  }, [rotationInterval, isPaused]);

  // Handle manual navigation
  const handleDotClick = (index) => {
    setCurrentViewIndex(index);
    setIsPaused(true); // Auto-pause when manually interacting
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const currentView = CALENDAR_TYPES[currentViewIndex];
  const targetMonth = addMonths(new Date(), currentView.monthOffset);

  // Filter events for current view
  const filteredEvents = events.filter(event => event.type === currentView.type);

  // Create event map by date
  const eventsByDate = filteredEvents.reduce((acc, event) => {
    const date = event.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  // Generate calendar days
  const monthStart = startOfMonth(targetMonth);
  const monthEnd = endOfMonth(targetMonth);

  // Calculate total needed cells to fill complete weeks
  const startDay = monthStart.getDay();
  // We need to go until the Saturday after the month end
  const endDay = monthEnd.getDay();
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get day names
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Create padding arrays
  const paddingStart = Array(startDay).fill(null);
  const paddingEnd = Array(6 - endDay).fill(null);

  // Combine into a flat array of all cells (should be multiple of 7)
  const allCells = [...paddingStart, ...daysInMonth, ...paddingEnd];

  // Chunk into weeks (arrays of 7)
  const weeks = [];
  for (let i = 0; i < allCells.length; i += 7) {
    weeks.push(allCells.slice(i, i + 7));
  }

  // Calculate row weights for "Smart Expansion"
  // We check the max number of events in any day of a week to determine that week's height needed
  const rowWeights = weeks.map(week => {
    if (!isFullScreen) return '1fr'; // Standard equal rows in normal mode

    const maxEventsInWeek = Math.max(...week.map(day => {
      if (!day) return 0;
      const dateStr = format(day, 'yyyy-MM-dd');
      return (eventsByDate[dateStr] || []).length;
    }));

    // "Smart scale" - Version 7 (Tuned Hybrid)
    /* 
       Logic:
       Use minmax(min-content, Xfr).
       Since 2 columns drastically reduces height, we don't need huge weights.
       We want to rely on min-content to fit strictly, and small weights to distribute leftovers.
       
       0 events: 0.5fr
       1-2 events: 1fr
       >4 events (2 cols): effective height is halved. 
          10 events -> 5 rows. 
          Compare to 2 events (standard) which is ~2 rows.
          So 10 events is ~2.5x height of standard.
          Let's try a shallower curve.
    */

    let weight = '1fr';
    if (maxEventsInWeek === 0) weight = '0.5fr';
    else if (maxEventsInWeek > 4) {
      // We have 2 columns.
      const effectiveCount = Math.ceil(maxEventsInWeek / 2);
      // Multiplier 0.15 allows gentle growth without exploding structure
      const rawWeight = 1 + ((effectiveCount - 2) * 0.15);
      weight = `${Math.min(rawWeight, 4)}fr`;
    } else if (maxEventsInWeek > 2) {
      // Single column, moderate growth
      const rawWeight = 1 + ((maxEventsInWeek - 2) * 0.4);
      weight = `${Math.min(rawWeight, 3)}fr`;
    }

    return `minmax(min-content, ${weight})`;
  });

  const gridTemplateRows = rowWeights.join(' ');
  const totalWeeks = weeks.length;

  // Calculate month name
  const monthName = format(targetMonth, 'MMMM yyyy');
  const viewSubtitle = currentView.monthOffset === 0 ? 'Current Month' : 'Next Month';

  // Helper to calculate event style based on count and screen mode
  const getEventStyle = (count) => {
    if (isFullScreen) {
      // Optimized for TV Mode - FULL WIDTH STACKING
      // Added flexShrink: 0 to PREVENT squashing. 
      // Added minHeight to ensure legibility.
      if (count <= 5) return { fontSize: '1rem', padding: '3px 6px', marginBottom: '4px', flexShrink: 0, minHeight: '28px' };
      if (count <= 8) return { fontSize: '0.9rem', padding: '2px 4px', marginBottom: '3px', flexShrink: 0, minHeight: '24px' };
      // Even for very many events, don't go too small
      return { fontSize: '0.85rem', padding: '1px 3px', marginBottom: '1px', flexShrink: 0, minHeight: '19px' };
    } else {
      // Standard fonts for desktop/laptop
      if (count <= 4) return { fontSize: '0.75rem', padding: '2px 4px' };
      if (count <= 6) return { fontSize: '0.7rem', padding: '1px 3px' };
      if (count <= 8) return { fontSize: '0.65rem', padding: '0px 2px' };
      return { fontSize: '0.6rem', padding: '0px 1px', lineHeight: '1' };
    }
  };

  return (
    <div className={`calendar-container ${isFullScreen ? 'fullscreen-mode' : ''}`} ref={containerRef}>
      <div className="calendar-header">
        {/* Slightly smaller header in fullscreen to prioritize grid space */}
        <h2 className="calendar-title" style={isFullScreen ? { fontSize: '2rem', marginBottom: '0.5rem' } : {}}>{currentView.title}</h2>
        <p className="calendar-subtitle" style={isFullScreen ? { fontSize: '1.25rem' } : {}}>{monthName} • {viewSubtitle}</p>
      </div>

      <div className="month-grid">
        <div className="month-header">
          {dayNames.map((day) => (
            <div key={day} className="day-name" style={isFullScreen ? { fontSize: '1.1em', padding: '0.5rem' } : {}}>{day}</div>
          ))}
        </div>

        <div className="days-grid" style={{
          gridTemplateRows: gridTemplateRows,
          // If simply 1fr everywhere, we can fallback to repeat, but the string above handles it.
          // Note: we must override the CSS defined generic grid-template-rows
        }}>
          {weeks.map((week, weekIndex) => (
            week.map((day, dayIndex) => {
              // If it's padding
              if (!day) {
                return <div key={`pad-${weekIndex}-${dayIndex}`} className="day-cell other-month"></div>;
              }

              const dateStr = format(day, 'yyyy-MM-dd');
              const dayEvents = eventsByDate[dateStr] || [];
              const isCurrentMonth = isSameMonth(day, targetMonth);
              const isTodayDate = isToday(day);

              // Check if we need multi-column layout (only in fullscreen with > 4 events)
              const isDense = isFullScreen && dayEvents.length > 4;

              const containerStyle = isDense ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '2px',
                alignContent: 'start'
              } : {};

              const eventStyle = getEventStyle(dayEvents.length);

              return (
                <div
                  key={dateStr}
                  className={`day-cell ${!isCurrentMonth ? 'other-month' : ''} ${isTodayDate ? 'today' : ''}`}
                >
                  <div className="day-number" style={isFullScreen ? { fontSize: '1.2rem', marginBottom: '0.5rem' } : {}}>{format(day, 'd')}</div>
                  <div className="events-container" style={containerStyle}>
                    {dayEvents.map((event, idx) => (
                      <div
                        key={`${event.id}-${idx}`}
                        className="event"
                        style={{
                          backgroundColor: event.color,
                          ...eventStyle,
                          // If split columns, ensure text doesn't overflow easily
                          ...(isDense ? {
                            fontSize: '0.8rem',
                            padding: '1px 3px',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden'
                          } : {})
                        }}
                        title={`${event.borrowerName} - ${event.lender} (${event.loanOfficer})`}
                      >
                        {event.title}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="legend">
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#EF4444' }}></div>
          <span className="legend-label">Samaritan Mortgage</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#1F2937' }}></div>
          <span className="legend-label">Magic Mortgage</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#3B82F6' }}></div>
          <span className="legend-label">Appli</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#EC4899' }}></div>
          <span className="legend-label">Appli - Ashley</span>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="bottom-controls" style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
          <button
            className="control-btn"
            onClick={() => setIsPaused(true)}
            title="Pause"
            style={{
              color: isPaused ? '#3b82f6' : 'inherit',
              borderColor: isPaused ? '#3b82f6' : 'inherit'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          </button>

          <div className="view-indicators">
            {CALENDAR_TYPES.map((_, index) => (
              <div
                key={index}
                className={`indicator-dot ${index === currentViewIndex ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
                title={CALENDAR_TYPES[index].title + (CALENDAR_TYPES[index].monthOffset ? ' (Next)' : ' (Current)')}
              />
            ))}
          </div>

          <button
            className="control-btn"
            onClick={() => setIsPaused(false)}
            title="Play"
            style={{
              color: !isPaused ? '#3b82f6' : 'inherit',
              borderColor: !isPaused ? '#3b82f6' : 'inherit'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        </div>

        {/* Fullscreen Button - Absolute Positioned Right */}
        <button
          className="control-btn"
          onClick={toggleFullScreen}
          title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          style={{ position: 'absolute', right: '1rem' }}
        >
          {isFullScreen ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-14v3h3v2h-5V5h2z" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default CalendarDisplay;
